<template>
  <CContainer
    class="pass-history-view appointment-pass-view datatable-container mb-5"
  >
    <Loader :is-processing="state.isLoading || areAppointmentPassesLoading" />
    <CRow class="mt-0 mt-sm-4">
      <CCol md="12">
        <div class="table-tabs-header bg-white mb-2 p-0">
          <div
            :class="{ 'show-hidden-tabs': state.showTabs }"
            class="d-flex align-items-center justify-content-between flex-wrap"
          >
            <DashboardTabs />
            <div
              class="d-none d-sm-flex actions align-items-center pt-0 p-sm-2"
            >
              <CButton
                v-show="isActiveDashboardUpdate"
                class="py-2 px-2 me-4 btn btn-inactive btn-sockets"
                @click="
                  $store.commit('dashboardTable/SET_DASHBOARD_UPDATES', false)
                "
              >
                <i class="fi flaticon-pause me-2"></i>Pause School Dashboard
              </CButton>
              <CButton
                v-show="!isActiveDashboardUpdate"
                class="py-2 px-2 me-4 btn bg-gradient-red text-white btn-sockets"
                @click="resumeDashboardUpdate"
              >
                <i class="fi flaticon-play me-2"></i>Resume School Dashboard
              </CButton>
              <CLink
                v-if="showDownloadCSVButton"
                class="lh-normal text-accent-blue-light"
                @click="exportAsCSV()"
              >
                <i class="fi flaticon-download me-1"></i>
                <span class="d-none d-md-inline-block">Download CSV</span>
              </CLink>
              <div
                class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
              >
                <CInputGroup class="w-auto">
                  <div class="input-group-prepend">
                    <span class="input-group-text lh-normal"
                      ><div><i class="flaticon-magnifying-glass"></i></div
                    ></span>
                  </div>
                  <CFormInput
                    class=""
                    placeholder="Search"
                    :value="filterData.search_query"
                    :disabled="state.isLoading"
                    @update:model-value="searchByQuery"
                  >
                  </CFormInput>
                </CInputGroup>
                <CButton
                  v-if="!isAppoinmentMode"
                  class="btn-advanced-search d-flex align-items-center"
                  @click="
                    state.isVisibleAdvancedSearch =
                      !state.isVisibleAdvancedSearch
                  "
                >
                  <i
                    class="fi me-1"
                    :class="
                      state.isVisibleAdvancedSearch
                        ? 'flaticon-minus'
                        : 'flaticon-plus'
                    "
                  ></i
                  >Advanced Search
                </CButton>
              </div>
            </div>
            <div
              class="m-auto p-2 d-block d-sm-none cursor-pointer"
              @click="state.showTabs = !state.showTabs"
            >
              {{ !state.showTabs ? "Show more" : "Show less" }}
            </div>
          </div>
          <div
            v-if="!isAppoinmentMode"
            v-show="state.isVisibleAdvancedSearch"
            class="advanced-search-container px-4 pb-3"
          >
            <CRow class="justify-content-center mt-4">
              <CCol md="4">
                <LazyLoadSelect
                  :is-multiple="true"
                  classes="mb-3"
                  :selected="filterData.by_students"
                  type="student"
                  placeholder="Search students"
                  @changed="
                    (value) => {
                      $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                        filter: 'by_students',
                        value: value
                      })
                    }
                  "
                />
              </CCol>
              <CCol md="4">
                <LazyLoadSelect
                  :is-multiple="true"
                  classes="mb-3"
                  :selected="filterData.by_teachers"
                  type="adults"
                  placeholder="Search teachers"
                  @changed="
                    (value) => {
                      $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                        filter: 'by_teachers',
                        value: value
                      })
                    }
                  "
                />
              </CCol>
              <CCol md="4">
                <LazyLoadSelect
                  :is-multiple="true"
                  :is-processing="false"
                  classes="mb-3"
                  :selected="filterData.by_rooms"
                  type="locations"
                  placeholder="Search rooms"
                  @changed="
                    (value) => {
                      $store.commit('dashboardTable/UPDATE_FILTERS_DATA', {
                        filter: 'by_rooms',
                        value: value
                      })
                    }
                  "
                />
              </CCol>
              <CCol md="12" class="mt-1 text-center text-sm-start">
                <div
                  v-if="!isQueueActiveAndStatusSelected"
                  class="d-inline-block text-center me-2"
                  @click="advancedFilter()"
                >
                  <CButton class="btn bg-gradient-blue search-btn text-white">
                    <div class="p-small">Search</div>
                  </CButton>
                </div>
                <div class="d-inline-block text-center">
                  <CButton class="btn me-2 clear-btn" @click="clearFilter()">
                    Clear
                  </CButton>
                </div>
              </CCol>
            </CRow>
          </div>
        </div>
        <template v-if="!isAppoinmentMode">
          <div
            v-if="isQueueActiveAndStatusSelected"
            class="d-flex gap-2 justify-content-between align-content-center flex-wrap my-4"
          >
            <div class="d-flex gap-4 flex-wrap">
              <DashboardStatusFilters />
              <CustomCheck
                label="Basic view"
                v-model="state.isBasicView"
                name="Basic view"
              />
              <CustomCheck
                label="Student Number"
                v-model="filterDataStudentNumber"
                name="student_sis_id"
              />
            </div>
            <div
              class="d-flex justify-content-end align-content-center gap-3 pe-1 flex-wrap"
            >
              <BaseButton
                v-if="activePassState === 'both'"
                rounded
                solid
                data-testid="clear-passes-button"
                @click="handleClearPasses"
              >
                Clear Passes
              </BaseButton>
              <BaseButton
                v-if="showClearQueueButton"
                rounded
                aria-label="Clear Queue passes"
                @click="onClearQueue"
              >
                Clear Queue
              </BaseButton>
            </div>
          </div>
          <div v-else>
            <div
              class="status-guides-container mb-2 flex-wrap d-flex justify-content-between align-items-center"
            >
              <DashboardStatusFilters />
              <div
                v-if="activePassState === 'both'"
                class="d-flex justify-content-end align-content-center gap-3 pe-1 flex-wrap"
              >
                <BaseButton
                  rounded
                  solid
                  data-testid="clear-passes-button"
                  @click="handleClearPasses"
                >
                  Clear Passes
                </BaseButton>
              </div>
              <DashboardTimeFilters />
              <div
                class="d-flex flex-column justify-content-center p-2 order-2 order-lg-2"
              >
                <div class="text-center mb-1">Basic View</div>
                <div class="d-flex align-items-center">
                  <span class="me-2">Off</span>
                  <CFormSwitch
                    id="basicViewSwitch"
                    switch-type="custom"
                    class="cs-switch mx-1"
                    shape="pill"
                    :checked="state.isBasicView"
                    aria-label="Toggle basic view of dashboard table"
                    @change="state.isBasicView = !state.isBasicView"
                  />
                  <span>On</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <DataTableOptimized
          v-if="!state.isLoading && !isAppoinmentMode"
          id="dashboardTable"
          class="datatable bg-white"
          :class="{ 'basic-view': state.isBasicView }"
          add-table-classes="datatable-table dashboard-table"
          :items="dashboardPasses"
          :fields="activeDataTableFields"
          :sorter="{ external: true }"
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          :basic-view="state.isBasicView"
          @update:sorter-value="sortByColumn"
          @row-clicked="setActiveRow"
        >
          <template #over-table>
            <HelpCenterButton
              classes="top-right mt-2 me-2"
              width="15"
              heigth="15"
              content-key="dashboard_datatable"
              :is-old="true"
            />
          </template>
          <template #student_sis_id="{ item, styles }">
            <div class="pass-time-info" :style="styles">
              {{
                item.user && item.user.student_sis_id
                  ? item.user.student_sis_id
                  : ""
              }}
            </div>
          </template>
          <template #user="{ item, styles }">
            <UserColumnData
              :pass="item"
              :is-basic-view="
                state.isBasicView && state.activeTableRow.item.id !== item.id
              "
              :style="styles"
            />
          </template>

          <template #queue_number="{ item, styles }">
            <div class="status-field" :style="styles">
              {{ item?.queue_number || 0 }}
            </div>
          </template>

          <template #badge_flags="{ item, styles }">
            <div class="status-field" :style="styles">
              <div
                v-if="getPassStatus(item)"
                :style="`color:${
                  getPassStatus(item).color ? getPassStatus(item).color : ''
                }`"
              >
                {{ getPassStatus(item).label }}
              </div>
            </div>
          </template>

          <template #queue_action="{ item, styles }">
            <div class="pass-time-info" :style="styles">
              <PassActionButtons
                :pass="item"
                :actions-status="999"
                :is-queue-mode="true"
              />
            </div>
          </template>

          <template #approved_at="{ item, styles }">
            <div class="pass-time-info align-top" :style="styles">
              <b>{{ item.from ? item.from.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.approved_at
                      ? $helpers.transformDate(item.approved_at, "h:mm A")
                      : ""
                  }}
                </p>
                <InOutColumn :pass="item" section="approved_at" />
                <PassActionButtons :pass="item" :actions-status="1" />
              </div>
            </div>
          </template>

          <template #completed_at="{ item, styles }">
            <div class="pass-time-info in-time align-top" :style="styles">
              <b>{{ item.to ? item.to.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.completed_at
                      ? $helpers.transformDate(item.completed_at, "h:mm A")
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="completed_at" />

                <PassActionButtons :pass="item" :actions-status="2" />
              </div>
            </div>
          </template>

          <template #approved_by="{ item, styles }">
            <div class="pass-time-info align-top" :style="styles">
              <b>{{
                item.child && item.child.from ? item.child.from.name : ""
              }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.child && item.child.approved_at
                      ? $helpers.transformDate(item.child.approved_at, "h:mm A")
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="child_approved_at" />
                <PassActionButtons :pass="item" :actions-status="3" />
              </div>
            </div>
          </template>

          <template #completed_by="{ item, styles }">
            <div class="pass-time-info in-time align-top" :style="styles">
              <b>{{ item.child && item.child.to ? item.child.to.name : "" }}</b>
              <div
                v-show="
                  !state.isBasicView || state.activeTableRow.item.id === item.id
                "
              >
                <p class="my-1 mx-0 p-0">
                  {{
                    item.child && item.child.completed_at
                      ? $helpers.transformDate(
                          item.child.completed_at,
                          "h:mm A"
                        )
                      : ""
                  }}
                </p>

                <InOutColumn :pass="item" section="child_completed_at" />

                <PassActionButtons :pass="item" :actions-status="4" />
              </div>
            </div>
          </template>

          <template #total_time="{ item, styles }">
            <div class="pass-time-info in-time align-top" :style="styles">
              <b>{{
                (item.total_time && item.badge_flags.ended) ||
                item.badge_flags.system_completed
                  ? item.total_time
                  : ""
              }}</b>
            </div>
          </template>

          <template #comments="{ item, styles }">
            <PassComments
              :pass="item"
              :is-basic-view="
                state.isBasicView && state.activeTableRow.item.id !== item.id
              "
              commentable-type="App\Models\Pass"
              :style="styles"
            />
          </template>

          <template #expired_at="{ item, styles }">
            <div :style="styles">
              {{
                item.expired_at ? $helpers.transformDate(item.expired_at) : ""
              }}
            </div>
          </template>
        </DataTableOptimized>
        <DashboardDataTableAptV6 v-if="isAppoinmentMode" ref="aptPassTable" />
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import { reactive, computed, onUnmounted, onMounted, watch, inject } from "vue"
import { useStore } from "vuex"
import UserColumnData from "@/v3components/Datatables/ColumnComponents/UserColumnData.vue"
import PassComments from "@/v3components/Datatables/ColumnComponents/PassComments.vue"
import PassActionButtons from "@/v3components/shared/PassComponents/PassActionButtons.vue"
import InOutColumn from "@/v3components/shared/PassComponents/InOutColumnOld.vue"
import LazyLoadSelect from "@/v3components/shared/Form/LazyLoadSelectOld.vue"
import DashboardTabs from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardTabsOld.vue"
import DashboardStatusFilters from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardStatusFiltersOld.vue"
import DashboardTimeFilters from "@/v3components/Datatables/DashboardDataTable/TabsAndFilter/DashboardTimeFiltersOld.vue"
import DashboardDataTableAptV6 from "@/v3components/Datatables/DashboardDataTable/DashboardDataTableAptV6Old.vue"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import CustomCheck from "@/v3components/shared/Form/CustomCheck.vue"
import useConfirmation from "@/composables/useConfirmation"
import helpers from "@/helpers/index.js"
import download from "@/helpers/downloadCSV.js"
import tableConfig from "./dashboardTableConfigOld.js"
import queueUtils from "@/helpers/queueUtils"
import useAppToast from "@/composables/useAppToast"

export default {
  name: "DashboardDataTableOld",
  components: {
    Loader,
    UserColumnData,
    PassComments,
    PassActionButtons,
    InOutColumn,
    LazyLoadSelect,
    HelpCenterButton,
    DashboardTabs,
    DashboardStatusFilters,
    DashboardTimeFilters,
    DashboardDataTableAptV6,
    BaseButton,
    CustomCheck
  },
  setup() {
    const modal = inject("modal")

    const toast = useAppToast()
    const store = useStore()
    const { showConfirmation } = useConfirmation(modal)

    const state = reactive({
      ...tableConfig,
      isBasicView: false,
      isLazyLoadingMode: false,
      isVisibleAdvancedSearch: false,
      modal: {
        isVisible: {
          editApptPass: false
        }
      },
      activeTableRow: { item: { id: null }, index: null },
      isLoading: false,
      showTabs: false,
      isInitialLoad: true
    })

    const periods = computed(() => store.getters["periods/formListStudents"])
    const editablePass = computed(
      () => store.getters["adultAptPass/editablePass"]
    )
    const passesTimeSettings = computed(
      () => store.getters["passes/passesTimeSettings"]
    )
    const currentUser = computed(() => store.getters["authentication/user"])
    const filterData = computed(
      () => store.getters["dashboardTable/filterData"]
    )
    const filterDataStudentNumber = computed({
      get() {
        return filterData?.value?.student_sis_id ? true : false
      },
      set(value) {
        store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
          {
            filter: "student_sis_id",
            value
          }
        ])
      }
    })

    const aptFilterData = computed(
      () => store.getters["dashboardTable/aptFilterData"]
    )
    const dashboardPasses = computed(() => {
      if (isQueueActiveAndStatusSelected.value) {
        return store.getters["dashboardTable/queuePasses"]
      }
      return store.getters["dashboardTable/passes"]
    })
    const isActiveDashboardUpdate = computed(
      () => store.getters["dashboardTable/isActiveDashboardUpdate"]
    )
    const activeSchoolHasAppoitmentPasses = computed(
      () => store.getters["schools/activeSchoolHasAppoitmentPasses"]
    )
    const isUserInTransparency = computed(
      () => store.getters["authentication/isUserInTransparency"]
    )
    const stats = computed(() => store.getters["dashboardTable/stats"])
    const activePassType = computed(
      () => store.getters["dashboardTable/getType"]
    )
    const activePassState = computed(
      () => store.getters["dashboardTable/getStatus"]
    )
    const school = computed(() => store.getters["schools/activeSchool"])
    const areAppointmentPassesLoading = computed(
      () => store.getters["adultAptPass/areAppointmentPassesLoading"]
    )

    const isAppoinmentMode = computed(() => {
      return activePassType.value.includes("appointment")
    })

    const activeDataTableFields = computed(() => {
      if (isQueueActiveAndStatusSelected.value) {
        return state.queueDataTableFields
          .filter((field) =>
            !state.isBasicView ? field.key !== "queue_number" : true
          )
          .filter((field) =>
            !filterData.value.student_sis_id
              ? field.key !== "student_sis_id"
              : field
          )
      }

      return state.dataTableFields
        .filter((field) =>
          !state.isBasicView ? field.key !== "badge_flags" : true
        )
        .filter((field) =>
          !filterData.value.student_sis_id
            ? field.key !== "student_sis_id"
            : field
        )
    })

    const userRole = computed(() => store.getters["authentication/getUserRole"])

    const isQueueModuleActive = computed(() => {
      return store.getters["queueSettings/isQueueModuleActive"]
    })

    const isQueueStatusSelected = computed(() => {
      return store.getters["dashboardTable/isQueueStatusSelected"]
    })

    const isQueueActiveAndStatusSelected = computed(() => {
      return store.getters["dashboardTable/isQueueActiveAndStatusSelected"]
    })

    const showClearQueueButton = computed(() => {
      if (!isQueueActiveAndStatusSelected.value) {
        return false
      }

      if (activePassType.value === "all") {
        return ["admin", "superadmin"].includes(userRole.value)
      } else {
        return true
      }
    })

    const showDownloadCSVButton = computed(() => {
      if (isQueueActiveAndStatusSelected.value) {
        return false
      }
      return true
    })

    const updateQueuePassesTable = computed(
      () => store.getters["dashboardTable/updateQueuePassesTable"]
    )

    const fetchQueuePasses = async () => {
      try {
        state.isLoading = true
        await store.dispatch("dashboardTable/getDashboardQueuePassesData", {
          type: activePassType.value
        })
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while fetching queue passes"
        toast.error(errorMessage)
      } finally {
        state.isLoading = false
      }
    }

    onUnmounted(() => {
      document.title = "securlypass"
    })

    onMounted(async () => {
      state.isLoading = true
      await store.dispatch("rooms/getAutoPassLimit")
      /**
       * Removed the below code as the data is already fetched in the view
       * File - src/v3views/DashboardOld.vue
       */
      // store.dispatch("dashboardTable/getDashboard")
      if (!passesTimeSettings.value) {
        await store.dispatch("passes/getPassesTimeSetting")
      }
      state.isLoading = false
    })

    watch(
      [() => activePassType?.value, () => activePassState?.value],
      async ([type, status]) => {
        if (
          [
            "appointments_today",
            "appointments_awaiting",
            "appointments_future_awaiting"
          ].includes(type)
        ) {
          return
        }

        if (isQueueActiveAndStatusSelected.value) {
          return await fetchQueuePasses()
        }

        state.isLoading = true
        await store.dispatch("dashboardTable/getDashboardPassesData", {
          type,
          status,
          isInitialLoad: state.isInitialLoad
        })
        if (state.isInitialLoad) {
          state.isInitialLoad = false
        }
        state.isLoading = false
      },
      { immediate: true }
    )

    watch(
      () => updateQueuePassesTable.value,
      async (value) => {
        if (value) {
          await fetchQueuePasses()
          store.commit("dashboardTable/SET_UPDATE_QUEUE_PASSES_TABLE", false)
        }
      }
    )

    const exportAsCSV = () => {
      if (isAppoinmentMode.value) {
        state.isLoading = true
        store
          .dispatch("adultAptPass/getCSVAppointmentPasses", {
            student_sis_id: aptFilterData.value.student_sis_id
          })
          .then((response) => {
            if (response.data) {
              download.CSVExport(response.data, "appointment-passes")
            }
            state.isLoading = false
          })
          .catch(() => {
            state.isLoading = false
          })
      } else {
        state.isLoading = true
        store
          .dispatch("dashboardTable/getDashboardPassesExport")
          .then((response) => {
            if (response.data) {
              download.CSVExport(response.data, "passes")
            }
            state.isLoading = false
          })
          .catch(() => {
            state.isLoading = false
          })
      }
    }

    const handleClearPasses = async () => {
      const confirmed = await showConfirmation({
        heading: "Clear Passes",
        headingColor: "red",
        headingIcon: "ri-alert-line",
        body: "Are you sure, this action will end all active passes which exceeds system ended time?<br><br><span class='bold-text'>Confirm Action :</span> This will clear any passes that have exceeded the System Ended time limit.",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        size: "md"
      })

      if (confirmed) {
        try {
          state.isLoading = true
          await store.dispatch("dashboardTable/clearExceedingPasses")
          await store.dispatch("dashboardTable/getDashboardPassesData")
          toast.success("Passes cleared successfully")
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Something went wrong while clearing passes"
          toast.error(errorMessage)
        } finally {
          state.isLoading = false
        }
      }
    }

    const clearFilter = () => {
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_students",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_teachers",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_rooms",
          value: []
        }
      ])
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", true)
      state.isLoading = true
      store
        .dispatch("dashboardTable/getDashboardPassesData", {
          isInitialLoad: false
        })
        .then(() => {
          state.isLoading = false
        })
    }

    const setPerPage = (option) => {
      if (option) {
        state.isLazyLoadingMode = option.label === "All entries"
        state.pagination.per_page = option
      }
    }

    const setActivePage = () => {
      helpers.scrollToTop()
    }

    const setActiveRow = (value, index, column, event) => {
      const path = event.path || (event.composedPath && event.composedPath())
      if (state.isBasicView) {
        let isModal = false
        if (path) {
          path.forEach((element) => {
            const classes = element.className
            if (classes && classes == "no-target") {
              isModal = true
            }
          })
        }

        const targetClasses = event.target.classList
        if (!targetClasses.contains("no-target") && !isModal) {
          const dashboardTable = document.getElementById("dashboardTable")
          const rows = dashboardTable.getElementsByTagName("tr")
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i]
            row.classList.remove("active")
          }
          if (
            state.activeTableRow &&
            state.activeTableRow.item.id === value.id
          ) {
            state.activeTableRow = { item: { id: null }, index: null }
          } else {
            state.activeTableRow = { item: value, index }
            if (rows[index + 1]) {
              rows[index + 1].classList.add("active")
            }
          }
        }
      }
    }

    const clearActiveRow = () => {
      const dashboardTable = document.getElementById("dashboardTable")
      const rows = dashboardTable.getElementsByTagName("tr")
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]
        row.classList.remove("active")
      }
    }

    const sortByColumn = (column) => {
      if (isQueueActiveAndStatusSelected.value) {
        const columnMap = {
          approved_at: "departing_from",
          completed_at: "destination"
        }
        const columnName = columnMap?.[column?.column] || column?.column
        store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
          filter: "sort",
          value: `${columnName}:${column.asc ? "asc" : "desc"}`
        })
        return
      }

      const columnName = state.columnSearchKeys[column.column]
        ? state.columnSearchKeys[column.column].sortBy
        : column.column
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "sort",
          value: `${columnName}:${column.asc ? "asc" : "desc"}`
        }
      ])
    }

    const updateSearchQuery = (value) => {
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
        filter: "search_query",
        value: value != "" ? value : null
      })
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "global_search",
          value: 1
        }
      ])
    }

    const searchByQuery = (value) => {
      if (window.searchByQueryTimeout) {
        clearTimeout(window.searchByQueryTimeout)
      }

      if (isQueueActiveAndStatusSelected.value) {
        return updateSearchQuery(value)
      }

      window.searchByQueryTimeout = setTimeout(() => {
        if (isAppoinmentMode.value) {
          store.commit("adultAptPass/SET_SEARCH_QUERY", value)
          state.isLoading = true
          updateSearchQuery(value)
          store.dispatch("adultAptPass/getAppointmentPasses").then(() => {
            setTimeout(() => {
              state.isLoading = false
            }, 500)
          })
        } else {
          updateSearchQuery(value)
          state.isLoading = true
          store
            .dispatch("dashboardTable/getDashboardPassesData", {
              isInitialLoad: false
            })
            .then(() => {
              setTimeout(() => {
                state.isLoading = false
              }, 500)
            })
        }
        if (isActiveDashboardUpdate.value == true) {
          store.commit("dashboardTable/SET_DASHBOARD_UPDATES", false)
        }
      }, 500)
    }

    const getPassStatus = (pass) => {
      if (pass.badge_flags) {
        if (pass.badge_flags.active && !pass.badge_flags.min_time) {
          return { label: "Active", class: "active", color: "#32ba7c" }
        }
        if (pass.badge_flags.ended) {
          return { label: "Ended", class: "ended" }
        }
        if (pass.badge_flags.system_completed) {
          return { label: "System Ended", class: "system-completed" }
        }
        if (pass.badge_flags.min_time) {
          return {
            label: passesTimeSettings.value
              ? helpers.formatDate(
                  passesTimeSettings.value.min_time,
                  "hh:mm:ss",
                  "m"
                ) + "+ MIN"
              : "Min time",
            class: "min-time",
            color: "#fff200"
          }
        }
        if (pass.badge_flags.extended) {
          return { label: "Extended", class: "extended" }
        }
        if (pass.badge_flags.missed) {
          return { label: "Missed", class: "missed", color: "#00a193" }
        }
        if (pass.badge_flags.canceled) {
          return { label: "Canceled", class: "canceled", color: "#7278e0" }
        }
      }
      return ""
    }

    const advancedFilter = () => {
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", false)
      state.isLoading = true
      store
        .dispatch("dashboardTable/getDashboardPassesData", {
          isInitialLoad: false
        })
        .then(() => {
          state.isLoading = false
        })
    }

    const resumeDashboardUpdate = async () => {
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_students",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_teachers",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", [
        {
          filter: "by_rooms",
          value: []
        }
      ])
      store.commit("dashboardTable/UPDATE_FILTERS_DATA", {
        filter: "search_query",
        value: ""
      })
      store.commit("adultAptPass/SET_SEARCH_QUERY", "")
      store.commit("dashboardTable/SET_DASHBOARD_UPDATES", true)
      if (isAppoinmentMode.value) {
        store.dispatch("adultAptPass/getAppointmentPasses")
      } else {
        if (isQueueActiveAndStatusSelected.value) {
          return await fetchQueuePasses()
        }
        store.dispatch("dashboardTable/getDashboardPassesData", {
          isInitialLoad: false
        })
      }
    }

    const onClearQueue = async () => {
      const { showConfirmation } = useConfirmation(modal)

      const body =
        activePassType?.value === "all"
          ? "Are you sure you would like to clear <span class='bold-text'>ALL PASS QUEUES</span> in the school? This would remove <span class='bold-text'>ALL PENDING REQUESTS</span> in queues and cannot be reversed."
          : "Are you sure you would like to clear all pass requests in your queues?"

      const shouldClear = await showConfirmation({
        heading: "Please confirm",
        headingIcon: "ri ri-alert-line",
        headingColor: "red",
        headerPlacement: "start",
        body,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        cancelButtonSolid: false,
        buttonPlacement: "end"
      })

      if (!shouldClear) return

      try {
        await store.dispatch("dashboardTable/clearQueuePasses", {
          type: activePassType.value
        })
        toast.success(
          activePassType?.value === "all"
            ? "All the school queue passes cleared successfully"
            : "Your queue passes cleared successfully"
        )
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong while clearing queue passes"
        toast.error(message)
      }
    }

    return {
      isQueueModuleActive,
      isPassStatusQueue: queueUtils.isPassStatusQueue,
      isQueueStatusSelected,
      isQueueActiveAndStatusSelected,
      showClearQueueButton,
      showDownloadCSVButton,
      filterDataStudentNumber,
      state,
      periods,
      editablePass,
      passesTimeSettings,
      areAppointmentPassesLoading,
      currentUser,
      filterData,
      aptFilterData,
      dashboardPasses,
      isActiveDashboardUpdate,
      activeSchoolHasAppoitmentPasses,
      isUserInTransparency,
      stats,
      activePassType,
      activePassState,
      school,
      isAppoinmentMode,
      activeDataTableFields,
      exportAsCSV,
      clearFilter,
      setPerPage,
      setActivePage,
      setActiveRow,
      clearActiveRow,
      sortByColumn,
      searchByQuery,
      getPassStatus,
      advancedFilter,
      resumeDashboardUpdate,
      onClearQueue,
      handleClearPasses
    }
  }
}
</script>
